import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { errorToast, successToast, ToastContext } from '@/components/Toast/Toast'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import { useUIVersion } from '@/utils/UIVersionContext'
import type { CompositeFilterDescriptor } from '@progress/kendo-data-query'
import type { FieldSettings } from '@progress/kendo-react-data-tools'
import type { CreateFilter } from 'api/Identity/Types/CreateFilter'
import type { UserFilterType } from 'api/Identity/Types/UserFilterType'
import { Filter, FilterType, FilterTypeName } from 'components/Filter'
import { createGridState, normalizeFilter } from 'features/DataGrid'
import type { GridState } from 'features/DataGrid/hooks/useGridState'
import {
  GridStateStatusActions,
  GridStateStatusState,
  useGridStatusState,
} from 'features/DataGrid/hooks/useGridStatusState'
import { ChevronDown, Search } from 'lucide-react'
import { useAppDispatch, useAppSelector } from 'store'
import type { FilterChangeEvent, GridFilterActionState } from 'store/grid'
import {
  fetchFilterAction,
  fetchFilterTypesAction,
  removeFilterAction,
  saveFilterAction,
  updateFilterAction,
} from 'store/identity/identity.actions'
import type { PageQuery } from 'utils/api'
import { useApiClient } from 'utils/api/ApiClient'
import { IdentityApiClient } from 'utils/api/identityApi'
import { AuthSessionContext } from 'utils/AuthContext'
import { isEmpty } from 'utils/is-empty'
import { buildQuery, buildSaveFilter } from 'utils/QueryHelpers'

import { SelectFilterEvent, SelectFilters } from './SelectFilters'

type GridStateLike = GridState | GridFilterActionState

type SetGridFiltersFn = (
  next: Partial<GridFilterActionState> | GridFilterActionState
) => void | Promise<void>

export type SelectFiltersFetchParams = PageQuery & {
  query?: string | null
  filter?: CompositeFilterDescriptor
}

interface SelectFiltersFacadeProps {
  setGridFilters: SetGridFiltersFn
  filterFetch: (params?: SelectFiltersFetchParams) => Promise<void>
  filterType?: FilterType
  filterFields: FieldSettings[]
  gridState: GridStateLike

  getQuery?: (filter?: CompositeFilterDescriptor) => string | null

  isSaveFilterDisabled?: boolean

  fetchMode?: 'auto' | 'manual'

  gridStatusState?: GridStateStatusState
  gridStatusActions?: GridStateStatusActions
  containerClassName?: string
  extraHeaderActions?: React.ReactNode
  showFilterWhenInactive?: boolean
}

export const SelectFiltersFacade: React.FC<SelectFiltersFacadeProps> = (props) => {
  const { isNewUI } = useUIVersion()
  const setToast = useContext(ToastContext)
  const { setGridFilters, gridState, filterType, filterFields, filterFetch } = props
  const fetchMode = props.fetchMode ?? 'auto'
  const [isLoading, setIsLoading] = useState(false)
  const internalStatus = useGridStatusState()
  const gridStatusState = props.gridStatusState ?? internalStatus.gridStatusState
  const gridStatusActions = props.gridStatusActions ?? internalStatus.gridStatusActions

  const { filters, filterTypes } = useMapStateToProps()
  const { fetchFilterTypes, fetchFilters, saveFilter, updateFilter, removeFilter } =
    useMapDispatchToProps()

  const [auth] = useContext(AuthSessionContext)

  useEffect(() => {
    if (isEmpty(filterTypes)) void fetchFilterTypes()
    if (isEmpty(filters)) void fetchFilters()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const setGrid = useCallback(
    async (next: Partial<GridFilterActionState> | GridFilterActionState) => {
      await Promise.resolve(setGridFilters(next))
    },
    [setGridFilters]
  )

  const safeParseFilter = useCallback((raw?: string): CompositeFilterDescriptor | undefined => {
    if (!raw) return undefined
    try {
      return JSON.parse(raw) as CompositeFilterDescriptor
    } catch (e) {
      console.warn('[SelectFiltersFacade] Failed to parse filter JSON:', e)
      return undefined
    }
  }, [])

  const getQueryFn = useMemo(
    () =>
      props.getQuery ?? ((f?: CompositeFilterDescriptor) => (f ? (buildQuery(f) ?? null) : null)),
    [props.getQuery]
  )

  const activeFilter = useMemo<CompositeFilterDescriptor>(
    () =>
      gridState.filter ?? {
        logic: 'and',
        filters: [],
      },
    [gridState.filter]
  )

  const buildFetchParams = useCallback(
    (
      filter?: CompositeFilterDescriptor,
      queryOverride?: string | null
    ): SelectFiltersFetchParams => {
      const query = queryOverride !== undefined ? queryOverride : getQueryFn(filter)
      return {
        filter,
        query,
        search: query,
        pageIndex: 1,
      }
    },
    [getQueryFn]
  )

  const getFilterType = useCallback(
    (types: UserFilterType[]): UserFilterType | undefined =>
      filterType
        ? (types.find((x) => x.id === filterType) ?? {
            id: filterType,
            name: FilterTypeName[FilterType[filterType] as keyof typeof FilterTypeName],
          })
        : undefined,
    [filterType]
  )

  const filterNameById = useCallback(
    (id: string) => {
      if (!filterType) return undefined
      return filters.filter((x) => x.filterType.id === filterType).find((x) => x.id === id)?.name
    },
    [filters, filterType]
  )

  const effectiveFilterName = useCallback(() => {
    const current = gridStatusState.gridFilterName
    return filterNameById(current) ?? current
  }, [filterNameById, gridStatusState.gridFilterName])

  const selectFilter = useCallback(
    async (event: { filterId: string }) => {
      const filterJson = filters.find((x) => x.id === event.filterId)
      if (!filterJson) return undefined

      const parsed = safeParseFilter(filterJson.data)
      if (!parsed) return undefined

      gridStatusActions.setGridFilterName(event.filterId)
      gridStatusActions.setFilterMode(true)
      gridStatusActions.setFilterSelected(true)

      await setGrid({
        filterName: event.filterId,
        filter: parsed,
      })

      return parsed
    },
    [filters, gridStatusActions, safeParseFilter, setGrid]
  )

  const fetchSelectedFilter = useCallback(
    async (event: { filterId: string }) => {
      const parsed = await selectFilter(event)
      if (!parsed) return

      if (fetchMode === 'manual') return

      await filterFetch(buildFetchParams(parsed))
    },
    [buildFetchParams, fetchMode, filterFetch, selectFilter]
  )

  const handleSelectFilter = useCallback(
    async (event: SelectFilterEvent) => fetchSelectedFilter({ filterId: event.filterId }),
    [fetchSelectedFilter]
  )

  const handleFilterName = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) =>
      gridStatusActions.setGridFilterName(event.target.value),
    [gridStatusActions]
  )

  const handleCreateFilter = useCallback(() => {
    gridStatusActions.setCreateMode(true)
    gridStatusActions.setFilterMode(true)
    gridStatusActions.setFilterSelected(false)
    gridStatusActions.setGridFilterName('')
  }, [gridStatusActions])

  const resetStatus = useCallback(() => {
    gridStatusActions.setFilterSelected(false)
    gridStatusActions.setCreateMode(false)
    gridStatusActions.setFilterMode(false)
    gridStatusActions.setGridFilterName('')
  }, [gridStatusActions])

  const resetGrid = useCallback(async () => {
    const nextGridState = createGridState({ skip: 0 }, {} as GridFilterActionState)
    resetStatus()
    await setGrid(nextGridState)
    return nextGridState
  }, [resetStatus, setGrid])

  const handleSaveFilter = useCallback(async () => {
    const ft = getFilterType(filterTypes)
    if (!ft || !auth) {
      console.log('[SelectFiltersFacade] FilterType not found or auth missing:', ft)
      return
    }

    const currentFilter = gridState.filter

    const data: CreateFilter = {
      name: gridStatusState.gridFilterName,
      data: currentFilter ? buildSaveFilter(currentFilter) : '',
      filterTypeId: ft.id,
      userId: auth.user.id,
    }

    const selectedFilterId = gridState.filterName
    const existingId = selectedFilterId
      ? filters.filter((x) => x.filterType.id === ft.id).find((x) => x.id === selectedFilterId)?.id
      : undefined

    try {
      if (existingId) {
        await updateFilter(existingId, data)
      } else {
        await saveFilter(data)
      }

      setToast(successToast('Filter saved successfully.'))

      await fetchFilters()
      const nextGridState = await resetGrid()
      await filterFetch(buildFetchParams(nextGridState.filter, null))
    } catch {
      setToast(errorToast('Failed to save filter.'))
    }
  }, [
    auth,
    buildFetchParams,
    fetchFilters,
    filterFetch,
    filterTypes,
    filters,
    getFilterType,
    gridState,
    gridStatusState.gridFilterName,
    resetGrid,
    saveFilter,
    updateFilter,
  ])

  const handleRemoveFilter = useCallback(async () => {
    const id = gridState.filterName
    if (!id) return

    try {
      await removeFilter(id)

      setToast(successToast('Filter removed successfully.'))

      const nextGridState = await resetGrid()
      await filterFetch(buildFetchParams(nextGridState.filter, null))
    } catch {
      setToast(errorToast('Failed to remove filter.'))
    }
  }, [buildFetchParams, filterFetch, gridState, removeFilter, resetGrid])

  const handleFilterChange = useCallback(
    async (event: FilterChangeEvent) => {
      gridStatusActions.setCreateMode(true)
      gridStatusActions.setFilterMode(true)
      gridStatusActions.setGridFilterName(effectiveFilterName())

      await setGrid({
        filter: normalizeFilter(event.filter),
      })
    },
    [effectiveFilterName, gridStatusActions, setGrid]
  )

  const handleResetFilter = useCallback(async () => {
    try {
      const nextGridState = await resetGrid()
      await filterFetch(buildFetchParams(nextGridState.filter, null))

      setToast(successToast('Filter cleared successfully.'))
    } catch {
      setToast(errorToast('Failed to clear filter.'))
    }
  }, [buildFetchParams, filterFetch, resetGrid, setToast])

  const handleFilterFetch = useCallback(async () => {
    try {
      setIsLoading(true)
      await filterFetch(buildFetchParams(activeFilter))
    } finally {
      setIsLoading(false)
    }
  }, [activeFilter, buildFetchParams, filterFetch])

  const canSave = gridStatusState.isFilterMode && (gridStatusState.gridFilterName?.length ?? 0) > 0
  const saveDisabled =
    !gridStatusState.isCreateMode || !canSave || Boolean(props.isSaveFilterDisabled)

  const renderSelect = () => {
    if (!filterType) return null

    if (gridStatusState.isCreateMode) {
      return (
        <Input
          placeholder='Filter name'
          value={gridStatusState.gridFilterName}
          onChange={handleFilterName}
          className={isNewUI ? 'h-9' : 'h-7'}
          data-test='filter-search-field'
        />
      )
    }

    return (
      <SelectFilters
        filterName={gridState.filterName}
        filterType={filterType}
        onChange={handleSelectFilter}
      />
    )
  }

  return (
    <>
      <div className='flex items-center justify-between'>
        <ButtonGroup>
          {renderSelect()}
          {filterType && (
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button
                    type='button'
                    variant='outline'
                    size={isNewUI ? 'md' : 'sm'}
                    aria-label='More options'
                  />
                }
              >
                More <ChevronDown />
              </DropdownMenuTrigger>
              <DropdownMenuContent data-test='more-filter-dropdown-block'>
                <DropdownMenuItem
                  data-test='create-filter-button'
                  disabled={gridStatusState.isCreateMode}
                  onClick={() => {
                    handleCreateFilter()
                  }}
                >
                  Create filter
                </DropdownMenuItem>

                <DropdownMenuItem
                  data-test='save-filter-button'
                  disabled={saveDisabled}
                  onClick={() => {
                    handleSaveFilter()
                  }}
                >
                  Save filter
                </DropdownMenuItem>

                <DropdownMenuItem
                  data-test='remove-filter-button'
                  disabled={!gridStatusState.isFilterSelected}
                  onClick={() => {
                    handleRemoveFilter()
                  }}
                >
                  Remove filter
                </DropdownMenuItem>

                <DropdownMenuItem
                  data-test='clear-filter-button'
                  onClick={() => {
                    handleResetFilter()
                  }}
                >
                  Clear
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <Button
            size={isNewUI ? 'md' : 'sm'}
            variant='outline'
            onClick={handleFilterFetch}
            disabled={isLoading}
            data-test='filter-search-button'
          >
            {isLoading ? <Spinner /> : <Search />}
            Filter Search
          </Button>
        </ButtonGroup>

        {props.extraHeaderActions}
      </div>

      <div className={props.containerClassName ?? 'wallet-table__filter'}>
        {(props.showFilterWhenInactive || gridStatusState.isFilterMode || !filterType) && (
          <Filter
            value={activeFilter}
            fields={filterFields}
            onChange={handleFilterChange}
            onKeyDown={handleFilterFetch}
          />
        )}
      </div>
    </>
  )
}

const useMapStateToProps = () => ({
  filters: useAppSelector((state) => state.identity.filter),
  filterTypes: useAppSelector((state) => state.identity.filterType),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()
  const identityApiClient = useApiClient(IdentityApiClient)

  return {
    fetchFilterTypes: async () => dispatch(fetchFilterTypesAction(identityApiClient)),
    fetchFilters: async () => dispatch(fetchFilterAction(identityApiClient)),
    saveFilter: async (data: CreateFilter) => dispatch(saveFilterAction(data, identityApiClient)),
    updateFilter: async (filterId: string, data: CreateFilter) =>
      dispatch(updateFilterAction(filterId, data, identityApiClient)),
    removeFilter: async (id: string) => dispatch(removeFilterAction(id, identityApiClient)),
  }
}
